import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';

import NotificationsEditListRowComponent from 'webapp/components/notifications/edit/list-row';

import type { BoardShare, Notification } from 'webapp/models-custom';
import Goal from 'webapp/models-custom/goal';
import MetricAnomaly from 'webapp/models-custom/metric-anomaly';
import Scorecard from 'webapp/models-custom/scorecard';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

interface Args {
	model: ModelWithNotificationForPopup;
	submitInProgress: boolean;
	edit: (notification: Notification) => void;
	delete: (notification: Notification) => void;
}

export default class NotificationsEditListComponent extends Component<Args> {
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	get selectedModelName() {
		return this.args.model.modelName;
	}

	@cached
	get allTargetModels() {
		return this.mmNotificationsService
			.getAllModelsWithPossibleNotifications()
			.filter((n) => !(n instanceof Goal));
	}

	private _allItems: Notification[] = [];

	@cached
	get allItems() {
		this.mmNotificationsService.updateModelCache(this._allItems, this.allTargetModels);
		return this._allItems.filter((i) => i.isValidForList);
	}

	@cached
	get filteredAndSortedItems() {
		const selectedModel = this.args.model;
		const selectedModelName = this.selectedModelName;
		return sortBy(
			this.allItems.filter((item) => {
				return (
					item.targetObject.modelName === selectedModelName &&
					this.mmNotificationsService.checkIfModelMatchesForList(
						selectedModel,
						item.targetObject as ModelWithNotificationForPopup,
					)
				);
			}),
			'frequencySortProp',
		);
	}

	get showTable() {
		const model = this.args.model;
		if (model instanceof Scorecard && !model.isDigest) {
			// no list for scorecards
			return false;
		}
		if (model instanceof MetricAnomaly) {
			// only one smart alert per metric setup, so no list is needed
			return false;
		}
		return this.filteredAndSortedItems.length > 0;
	}

	get title() {
		const selectedModelName = this.selectedModelName;
		let title = '';
		if (selectedModelName === ModelName.REMINDER) {
			title = 'Reminders for this report';
		} else {
			title = 'Schedules for this ';
			switch (selectedModelName) {
				case ModelName.SCORECARD:
					title = 'Weekly updates for this metric'; // for digest only. scorecards have no list
					break;
				case ModelName.ALERT_SETTING:
					title = 'Manual alerts for this metric';
					break;
				case ModelName.METRIC_ANOMALY:
					title = 'Smart alerts for this metric';
					break;
				case ModelName.BOARD_SHARE:
					if ((this.args.model as BoardShare).isLoop) {
						title += 'looped dashboard';
					} else {
						title += 'dashboard';
					}
					break;
				case ModelName.REPORT_SCHEDULE_SETTING:
					title += 'report';
					break;
			}
		}
		const itemsCount = this.filteredAndSortedItems.length;
		return `${title} (${itemsCount})`;
	}

	<template>
		{{#if this.showTable}}
			<div class="list-ct">
				<div class="bottom-title">{{this.title}}</div>
				<table class="bigtable bigtable--no-sticky-header flexible-table">
					<colgroup>
						<col width="30%" />
						<col />
						<col />
						<col width="200" />
						<col width="100" />
					</colgroup>
					<thead>
						<th>Frequency</th>
						<th class="prio-low">Delivery</th>
						<th class="prio-low">Recipients</th>
						<th class="prio-medium">Active for me</th>
						<th>Actions</th>
					</thead>
					<tbody>
						{{#each this.filteredAndSortedItems as |item|}}
							<NotificationsEditListRowComponent
								@model={{item}}
								@selectedTargetObject={{@model}}
								@isDisabled={{@submitInProgress}}
								@edit={{@edit}}
								@delete={{@delete}}
							/>
						{{/each}}
					</tbody>
				</table>
			</div>
		{{/if}}
	</template>
}
